import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Upload,
  Sparkle,
  CheckCircle,
  ArrowRight,
  Spinner,
  FileXls,
  SkipForward,
  Warning,
  X,
} from "@phosphor-icons/react";
import { useAuth, API } from "@/contexts/AuthContext";
import { PlantusLogo } from "@/components/plantus/Logo";

/**
 * Onboarding direkt nach der Registrierung.
 * - Step 1: optionaler Excel/CSV-Import (mit "Ueberspringen"-Button)
 * - Step 2: KI-Mapping pruefen
 * - Step 3: Ergebnis + weiter zum Dashboard
 */
export default function OnboardingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [activeSheetIdx, setActiveSheetIdx] = useState(0);
  const [overrides, setOverrides] = useState({});
  const [commitResult, setCommitResult] = useState(null);

  useEffect(() => {
    document.title = "Plantus · Willkommen";
  }, []);

  const skipOnboarding = () => navigate("/app", { replace: true });

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 8 MB).");
      return;
    }
    setFile(f);
  };

  const startAnalyze = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/app/import/analyze`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      const init = {};
      data.sheets.forEach((sh, idx) => {
        init[idx] = {};
        sh.mappings.forEach((m) => (init[idx][m.source] = m.target));
      });
      setOverrides(init);
      setAnalysis(data);
      setStep(2);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Analyse fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const activeSheet = analysis?.sheets?.[activeSheetIdx];
  const targets =
    activeSheet?.data_type === "applications"
      ? analysis.application_targets
      : analysis?.field_targets || {};

  const updateMapping = (source, target) => {
    setOverrides((o) => ({...o,
      [activeSheetIdx]: {...(o[activeSheetIdx] || {}), [source]: target || null },
    }));
  };

  const updateDataType = (newType) => {
    setAnalysis((a) => {
      const sheets = a.sheets.slice();
      sheets[activeSheetIdx] = {...sheets[activeSheetIdx], data_type: newType };
      return {...a, sheets };
    });
  };

  const startCommit = async () => {
    if (!activeSheet) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("data_type", activeSheet.data_type);
      fd.append("sheet_name", activeSheet.sheet_name);
      fd.append("mapping", JSON.stringify(overrides[activeSheetIdx] || {}));
      const { data } = await axios.post(`${API}/app/import/commit-file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setCommitResult(data);
      setStep(3);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 402) {
        const detail = typeof d === "object" ? d : {};
        toast.error(detail.message || "Free-Limit erreicht.", {
          action: { label: "Upgrade", onClick: () => navigate("/preise") },
          duration: 10000,
        });
      } else {
        toast.error(typeof d === "string" ? d : "Import fehlgeschlagen.");
      }
    } finally {
      setBusy(false);
    }
  };

  const firstName = user?.name?.split(" ")[0] || "";

  return (
    <div className="min-h-screen bg-bone" data-testid="onboarding-page">
      <header className="border-b border-bone-200/70 px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/app">
          <PlantusLogo size="lg" />
        </Link>
        <button
          onClick={skipOnboarding}
          data-testid="onboarding-skip"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#7A827C] hover:text-forest-700 px-3 py-2 rounded-full"
        >
          Überspringen <SkipForward size={14} weight="bold" />
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        {step === 1 && (
          <section className="text-center" data-testid="onboarding-step-1">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-forest-50 text-forest-700 text-xs font-semibold uppercase tracking-[0.18em] mb-4">
              <Sparkle size={12} weight="fill" /> Willkommen{firstName ? `, ${firstName}` : ""}
            </div>
            <h1 className="font-heading text-3xl sm:text-5xl font-semibold text-forest-900 tracking-tight">
              Hast du schon Daten?
            </h1>
            <p className="mt-4 font-body text-base sm:text-lg text-[#4A524C] max-w-xl mx-auto">
              Lade deine bestehende Excel- oder CSV-Liste hoch. Plantus erkennt automatisch was darin steht und legt deine Flächen oder Anwendungen für dich an.
              <br />
              <span className="text-sm text-[#7A827C]">Du kannst diesen Schritt jederzeit überspringen.</span>
            </p>

            <div className="mt-10 max-w-xl mx-auto bg-white rounded-3xl border border-bone-200 p-6 sm:p-8">
              <label
                className="block cursor-pointer rounded-2xl border-2 border-dashed border-forest-200 bg-bone/40 hover:bg-bone/70 hover:border-forest-300 transition px-6 py-12"
                data-testid="onboarding-dropzone"
              >
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={onPick}
                  className="hidden"
                  data-testid="onboarding-file-input"
                />
                {file ? (
                  <div className="flex items-center justify-center gap-3 text-forest-900">
                    <FileXls size={28} weight="duotone" />
                    <div className="text-left">
                      <div className="font-medium">{file.name}</div>
                      <div className="text-xs text-[#7A827C]">{Math.round(file.size / 1024)} KB · klicke für andere Datei</div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Upload size={36} weight="duotone" className="text-forest-700 mx-auto" />
                    <div className="mt-3 font-heading font-semibold text-forest-900">Excel oder CSV hierhin ziehen</div>
                    <div className="text-sm text-[#4A524C] mt-1">oder klicken um Datei auszuwählen · max. 8 MB</div>
                  </div>
                )}
              </label>

              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <button
                  onClick={startAnalyze}
                  disabled={!file || busy}
                  data-testid="onboarding-analyze-btn"
                  className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 disabled:bg-forest-600/40 text-white font-medium text-sm px-6 py-3 rounded-full shadow-sm hover:shadow-md transition-all"
                >
                  {busy ? (
                    <>
                      <Spinner size={16} className="animate-spin" /> Plantus analysiert deine Datei…
                    </>
                  ) : (
                    <>
                      <Sparkle size={16} weight="fill" /> KI-Analyse starten
                    </>
                  )}
                </button>
                <button
                  onClick={skipOnboarding}
                  data-testid="onboarding-start-empty"
                  className="inline-flex items-center gap-2 border border-forest-600/30 text-forest-700 hover:bg-forest-50 font-medium text-sm px-5 py-3 rounded-full"
                >
                  Mit leerem Konto starten
                </button>
              </div>
            </div>

            <div className="mt-12 grid sm:grid-cols-3 gap-4 text-left max-w-3xl mx-auto">
              <Bullet num="1" title="Liste hochladen" body="Excel, CSV oder Spritztagebuch-Export deiner alten Software." />
              <Bullet num="2" title="KI mappt automatisch" body="Plantus erkennt Spalten wie 'Schlag', 'Mittel', 'Datum'. du prüfst nur kurz." />
              <Bullet num="3" title="Sofort startklar" body="Statt 100 Flächen einzutippen, hast du sie in unter 1 Minute drin." />
            </div>
          </section>
        )}

        {step === 2 && activeSheet && (
          <section className="space-y-5" data-testid="onboarding-step-2">
            {analysis.sheets.length > 1 && (
              <div className="flex gap-2 flex-wrap">
                {analysis.sheets.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveSheetIdx(idx)}
                    className={`px-4 py-2 rounded-full text-sm font-medium ${
                      activeSheetIdx === idx
                        ? "bg-forest-900 text-white"
                        : "bg-white border border-bone-200 text-forest-700 hover:bg-bone"
                    }`}
                  >
                    {s.sheet_name} · {s.total_rows} Zeilen
                  </button>
                ))}
              </div>
            )}

            <div className="bg-white rounded-3xl border border-bone-200 p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-forest-700">
                    <Sparkle size={14} weight="fill" /> KI-Vorschlag (Konfidenz {Math.round((activeSheet.confidence || 0) * 100)} %)
                  </div>
                  <h2 className="font-heading text-2xl font-semibold text-forest-900 mt-1">
                    Plantus hat erkannt:
                    <span className="ml-2 inline-flex items-center gap-1 text-base font-semibold bg-forest-50 text-forest-700 px-3 py-1 rounded-full">
                      {activeSheet.data_type === "fields"
                        ? "Flächen / Schläge"
                        : activeSheet.data_type === "applications"
                        ? "Anwendungen / Spritztagebuch"
                        : "Unbekannt"}
                    </span>
                  </h2>
                  {activeSheet.suggestion && (
                    <p className="text-sm text-[#4A524C] mt-2 max-w-2xl">{activeSheet.suggestion}</p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#7A827C]">Daten-Typ:</span>
                  <select
                    value={activeSheet.data_type}
                    onChange={(e) => updateDataType(e.target.value)}
                    className="rounded-full border border-bone-200 bg-white px-3 py-1.5 text-sm"
                    data-testid="onboarding-datatype-select"
                  >
                    <option value="fields">Flächen</option>
                    <option value="applications">Anwendungen</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 overflow-x-auto" data-testid="onboarding-mapping-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-[0.16em] text-[#7A827C] border-b border-bone-200">
                      <th className="text-left py-2 pr-4">Aus deiner Datei</th>
                      <th className="text-left py-2 pr-4">Plantus-Feld</th>
                      <th className="text-left py-2 pr-4">Konfidenz</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeSheet.mappings.map((m, i) => {
                      const current = overrides[activeSheetIdx]?.[m.source] ?? null;
                      const conf = Math.round((m.confidence || 0) * 100);
                      return (
                        <tr key={i} className="border-b border-bone-100">
                          <td className="py-3 pr-4 font-medium text-forest-900">{m.source}</td>
                          <td className="py-3 pr-4">
                            <select
                              value={current || ""}
                              onChange={(e) => updateMapping(m.source, e.target.value || null)}
                              className="rounded-lg border border-bone-200 bg-white px-2 py-1.5 text-sm w-full max-w-xs"
                              data-testid={`onboarding-mapping-${i}`}
                            >
                              <option value="">, nicht importieren,</option>
                              {Object.entries(targets).map(([key, label]) => (
                                <option key={key} value={key}>
                                  {label}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="py-3 pr-4">
                            <ConfidenceBadge conf={conf} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-7 flex flex-wrap gap-3 justify-between items-center">
                <button
                  onClick={() => {
                    setStep(1);
                    setAnalysis(null);
                  }}
                  className="text-sm text-[#4A524C] hover:underline"
                  data-testid="onboarding-back"
                >
                  ← Andere Datei wählen
                </button>
                <button
                  onClick={startCommit}
                  disabled={busy}
                  data-testid="onboarding-commit-btn"
                  className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 disabled:bg-forest-600/40 text-white font-medium text-sm px-6 py-3 rounded-full shadow-sm hover:shadow-md transition-all"
                >
                  {busy ? (
                    <>
                      <Spinner size={16} className="animate-spin" /> Importiere…
                    </>
                  ) : (
                    <>
                      {activeSheet.total_rows} Zeilen übernehmen <ArrowRight size={16} weight="bold" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </section>
        )}

        {step === 3 && commitResult && (
          <section className="bg-white rounded-3xl border border-bone-200 p-8 sm:p-12 text-center" data-testid="onboarding-step-3">
            <div className="mx-auto w-20 h-20 rounded-2xl bg-forest-50 text-forest-700 flex items-center justify-center mb-4">
              <CheckCircle size={36} weight="fill" className="text-forest-600" />
            </div>
            <h2 className="font-heading text-3xl font-semibold text-forest-900">Geschafft!</h2>
            <p className="text-base text-[#4A524C] mt-2">
              Plantus hat <strong>{commitResult.inserted} {commitResult.data_type === "fields" ? "Flächen" : "Anwendungen"}</strong> für dich angelegt.
              Du kannst direkt loslegen.
            </p>

            <div className="mt-6 grid grid-cols-3 gap-3 text-sm max-w-md mx-auto">
              <Stat label="Importiert" value={commitResult.inserted} color="green" />
              <Stat label="Übersprungen" value={commitResult.skipped?.length || 0} color="amber" />
              <Stat label="Fehler" value={commitResult.errors?.length || 0} color="red" />
            </div>

            {(commitResult.skipped?.length > 0 || commitResult.errors?.length > 0) && (
              <details className="mt-6 text-left bg-bone/40 rounded-2xl p-4 max-w-md mx-auto">
                <summary className="cursor-pointer text-sm font-medium text-forest-700">Details anzeigen</summary>
                <div className="mt-3 max-h-60 overflow-y-auto text-xs space-y-2">
                  {commitResult.skipped?.map((s, i) => (
                    <div key={`s${i}`} className="flex items-start gap-2 text-amber-900">
                      <Warning size={14} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
                      <span>Zeile {s.row}: {s.reason}</span>
                    </div>
                  ))}
                  {commitResult.errors?.map((e, i) => (
                    <div key={`e${i}`} className="flex items-start gap-2 text-red-900">
                      <X size={14} weight="bold" className="text-red-600 mt-0.5 shrink-0" />
                      <span>Zeile {e.row}: {e.reason}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}

            <button
              onClick={() => navigate("/app", { replace: true })}
              data-testid="onboarding-finish"
              className="mt-8 inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white font-medium text-sm px-6 py-3 rounded-full shadow-sm hover:shadow-md transition-all"
            >
              Zur Übersicht <ArrowRight size={16} weight="bold" />
            </button>
          </section>
        )}
      </main>
    </div>
  );
}

const Bullet = ({ num, title, body }) => (
  <div className="bg-white rounded-2xl border border-bone-200 p-5">
    <div className="w-8 h-8 rounded-full bg-forest-900 text-white font-heading font-semibold flex items-center justify-center text-sm">
      {num}
    </div>
    <div className="font-heading font-semibold text-forest-900 mt-3">{title}</div>
    <div className="text-sm text-[#4A524C] mt-1">{body}</div>
  </div>
);

const ConfidenceBadge = ({ conf }) => {
  const tone =
    conf >= 80 ? "bg-emerald-50 text-emerald-800" : conf >= 50 ? "bg-amber-50 text-amber-800" : "bg-red-50 text-red-800";
  return <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full ${tone}`}>{conf}%</span>;
};

const Stat = ({ label, value, color }) => {
  const tone =
    color === "green"
      ? "bg-emerald-50 text-emerald-900 border-emerald-200"
      : color === "amber"
      ? "bg-amber-50 text-amber-900 border-amber-200"
      : "bg-red-50 text-red-900 border-red-200";
  return (
    <div className={`rounded-2xl border p-4 ${tone}`}>
      <div className="text-3xl font-heading font-semibold">{value}</div>
      <div className="text-xs uppercase tracking-[0.18em] mt-1 opacity-80">{label}</div>
    </div>
  );
};
